import Backdrop from '@material-ui/core/Backdrop';
import Box from '@material-ui/core/Box';
import Fade from '@material-ui/core/Fade';
import Modal from '@material-ui/core/Modal';
import { makeStyles } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Utils from '../../commons/utils/Utils';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '95%',
    maxWidth: '480px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: '8px',
};

const useStyles = makeStyles(() => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
}));

/**
 * Modal de exclusión de un vehículo de la grilla del programa (VEC-3333). El motivo se elige de un
 * catálogo administrable (`/motivos`, tipo "Exclusión de Programa Preventivo") — lo da de alta el
 * usuario, no viene fijo en el código. El comentario es siempre opcional por ahora.
 *
 * Props:
 *   open        {boolean}
 *   vehiculo    {object}   - fila de la grilla (id, movil_dominio, movil_unidad, region, delta_km, fecha_sugerida)
 *   onClose     {function}
 *   onConfirmar {function(motivoId, comentario)} - ejecuta la exclusión (API o estado local del preview)
 *                            y devuelve una Promise; el modal cierra solo si resuelve.
 */
const ProgramasPreventivosExclusionModal = (props) => {
    const intl = useIntl();
    const classes = useStyles();
    const [motivos, setMotivos] = useState([]);
    const [motivoId, setMotivoId] = useState('');
    const [comentario, setComentario] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!props.open) return;
        setMotivoId('');
        setComentario('');
        Utils.getData('/programas-preventivos/motivos-exclusion')
            .then(function(data) { setMotivos(data || []); })
            .catch(function() { setMotivos([]); });
    }, [props.open, props.vehiculo]);

    if (!props.vehiculo) return null;

    var puedeConfirmar = motivoId !== '';

    var handleConfirmar = function() {
        if (!puedeConfirmar || loading) return;
        setLoading(true);
        var motivoSeleccionado = motivos.find(function(m) { return String(m.id) === String(motivoId); });
        props.onConfirmar(
            parseInt(motivoId, 10),
            comentario || null,
            motivoSeleccionado ? motivoSeleccionado.nombre : ''
        )
            .then(function() {
                setLoading(false);
                props.onClose();
            })
            .catch(function() {
                setLoading(false);
            });
    };

    return (
        <Modal
            open={props.open}
            onClose={props.onClose}
            className={classes.modal}
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={props.open}>
                <Box sx={modalStyle}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="mb-0">
                            <i className="fa fa-ban text-danger"></i>{' '}
                            <FormattedMessage id="programasPreventivosExclusion.titulo" defaultMessage="Excluir unidad del programa" />
                        </h4>
                        <button type="button" className="close" onClick={props.onClose} aria-label="close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <div className="p-2 mb-3" style={{ background: '#f8f9fa', borderRadius: '4px' }}>
                        <strong>
                            {props.vehiculo.movil_dominio}
                            {props.vehiculo.movil_unidad ? ' · ' + props.vehiculo.movil_unidad : ''}
                        </strong>
                        <div className="text-muted" style={{ fontSize: '13px' }}>
                            {[
                                props.vehiculo.region,
                                props.vehiculo.delta_km !== null && props.vehiculo.delta_km !== undefined
                                    ? intl.formatMessage(
                                        { id: 'programasPreventivosExclusion.resumen_delta', defaultMessage: 'Delta: {delta} km' },
                                        { delta: props.vehiculo.delta_km.toLocaleString('es-AR') }
                                    )
                                    : null,
                                props.vehiculo.fecha_sugerida
                                    ? intl.formatMessage(
                                        { id: 'programasPreventivosExclusion.resumen_fecha_sugerida', defaultMessage: 'Fecha sugerida: {fecha}' },
                                        { fecha: props.vehiculo.fecha_sugerida }
                                    )
                                    : null,
                            ].filter(Boolean).join(' · ')}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>
                            <FormattedMessage id="programasPreventivosExclusion.label_motivo" defaultMessage="Motivo de exclusión" /> *
                        </label>
                        <select
                            className="form-control"
                            value={motivoId}
                            onChange={function(e) { setMotivoId(e.target.value); }}
                        >
                            <option value="">
                                {intl.formatMessage({ id: 'programasPreventivosExclusion.placeholder_motivo', defaultMessage: 'Seleccionar motivo...' })}
                            </option>
                            {motivos.map(function(m) {
                                return <option key={m.id} value={m.id}>{m.nombre}</option>;
                            })}
                        </select>
                        {motivos.length === 0 && (
                            <small className="text-muted">
                                <FormattedMessage
                                    id="programasPreventivosExclusion.sin_motivos"
                                    defaultMessage="No hay motivos configurados. Creálos desde la pantalla de Motivos."
                                />
                            </small>
                        )}
                    </div>

                    <div className="form-group">
                        <label>
                            <FormattedMessage id="programasPreventivosExclusion.label_comentario" defaultMessage="Comentario" />{' '}
                            <span className="text-muted">
                                (<FormattedMessage id="programasPreventivosExclusion.label_comentario_opcional" defaultMessage="opcional" />)
                            </span>
                        </label>
                        <textarea
                            className="form-control"
                            rows={3}
                            value={comentario}
                            onChange={function(e) { setComentario(e.target.value); }}
                        />
                    </div>

                    <div className="text-right mt-3">
                        <button type="button" className="btn btn-secondary mr-1" onClick={props.onClose} disabled={loading}>
                            <FormattedMessage id="programasPreventivosExclusion.btn_cancelar" defaultMessage="Cancelar" />
                        </button>
                        <button type="button" className="btn btn-danger" onClick={handleConfirmar} disabled={!puedeConfirmar || loading}>
                            <i className="fa fa-ban"></i>{' '}
                            <FormattedMessage id="programasPreventivosExclusion.btn_confirmar" defaultMessage="Confirmar exclusión" />
                        </button>
                    </div>
                </Box>
            </Fade>
        </Modal>
    );
};

export default ProgramasPreventivosExclusionModal;
